import { inject, Injectable } from '@angular/core';
import {
  CRMMultiLocationContactApiService,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFieldOptionsResponseInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  ListMultiLocationCrmObjectsRequest,
  ListMultiLocationFieldOptionsRequest,
  ListMultiLocationFiltersRequest,
} from '@vendasta/crm';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { CrmMultilocationInjectionToken, MultiLocationContext } from '../../../tokens-and-interfaces';
import { ContactService } from './_contact.service';

@Injectable({ providedIn: 'root' })
export class MultiLocationContactService extends ContactService {
  private multiLocationContactService = inject(CRMMultiLocationContactApiService);
  private multiLocationConfig = inject(CrmMultilocationInjectionToken);

  override listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          sortBy: request.sortBy || [],
          filtersV2: request.filtersV2 || {},
          pagingOptions: request.pagingOptions || {},
        } as ListMultiLocationCrmObjectsRequest;
        return this.multiLocationContactService.listMultiLocationContacts(req);
      }),
      map((response) => {
        return {
          crmObjects: response.crmObjects || [],
          pagingMetadata: response.pagingMetadata || {},
        } as ListCrmObjectsResponseInterface;
      }),
    );
  }

  override listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          searchTerm: request.searchTerm || '',
          paging: request.paging || {},
        } as ListMultiLocationFiltersRequest;
        return this.multiLocationContactService.listMultiLocationContactFilters(req);
      }),
      map((response) => {
        return {
          filters: response.filters || [],
          paging: response.paging || {},
        } as ListFiltersResponseInterface;
      }),
    );
  }

  override listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<ListFieldOptionsResponseInterface> {
    return (this.multiLocationConfig.multiLocationContext$ ?? of({} as MultiLocationContext)).pipe(
      switchMap((multiLocationContext) => {
        const req = {
          namespaces: multiLocationContext.accountGroupsIds || [],
          fieldId: request.fieldId || '',
          searchTerm: request.searchTerm || '',
          pageSize: request.pageSize || 0,
        } as ListMultiLocationFieldOptionsRequest;
        return this.multiLocationContactService.listMultiLocationContactFieldOptions(req);
      }),
      map((response) => {
        return {
          options: response.options || [],
        } as ListFieldOptionsResponseInterface;
      }),
    );
  }
}
